/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { useTheme } from '../theme/ThemeContext';

/**
 * VelLogo Component
 * 
 * Displays the Vel SVG with a sleek, polished look.
 * 
 * Props:
 * - size: (number) height of the logo in pixels. Width scales automatically maintain aspect ratio 1:1.
 * - variant: 'default' | 'white'
 *   'default' -> uses the rich purple/gold gradients.
 *   'white'   -> uses a pure white gradient/fill for dark backgrounds.
 */
const VelLogo = ({ size = 40, variant = 'default', style = {}, themeOverride = null }) => {
    const { theme: contextTheme } = useTheme();
    // Callers that render on a screen pinned to one palette (the sign-in page)
    // pass themeOverride so the mark matches its surroundings instead of the
    // application-wide theme. Everyone else keeps the context theme.
    const theme = themeOverride || contextTheme;
    // Aspect ratio 1:1 (256x256)
    const width = size;
    const height = size;

    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            style={{ filter: variant === 'default' ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' : 'none', ...style }}
        >
            <defs>
                {/* Leaf gradient - Dynamic Theme Colors */}
                <linearGradient id="leafGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={theme.primary} />
                    <stop offset="60%" stopColor={theme.secondary} />
                    <stop offset="100%" stopColor={theme.secondary} stopOpacity="0.6" />
                </linearGradient>

                {/* Handle gradient - Metallic Dark or Dark Grey */}
                <linearGradient id="handleGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2a2a2a" />
                    <stop offset="100%" stopColor="#000000" />
                </linearGradient>

                {/* Ring gradient - Metallic */}
                <linearGradient id="ringGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#525252" />
                    <stop offset="100%" stopColor="#171717" />
                </linearGradient>

                {/* White Variant Gradients */}
                <linearGradient id="whiteLeafGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="100%" stopColor="#E0E0E0" />
                </linearGradient>
            </defs>

            {/* Vel Leaf - Ultra Sharp & Aerodynamic with Bigger Base */}
            <path
                d="
                  M128 2
                  C 180 50 215 140 170 200
                  C 160 212 150 220 148 224
                  L 108 224
                  C 106 220 96 212 86 200
                  C 41 140 76 50 128 2
                  Z
                "
                fill={variant === 'white' ? "url(#whiteLeafGradient)" : "url(#leafGradient)"}
            />

            {/* Central Energy Groove - Thinner & Sharper */}
            <path
                d="M128 15 L128 215"
                stroke={variant === 'white' ? "rgba(0,0,0,0.15)" : "#f3e8ff"}
                strokeWidth="2"
                strokeLinecap="round"
                opacity={variant === 'white' ? "0.3" : "0.7"}
            />

            {/* Ring (Metallic Anchor) */}
            <ellipse
                cx="128"
                cy="222"
                rx="14"
                ry="4"
                fill={variant === 'white' ? "#FFFFFF" : "url(#ringGradient)"}
                fillOpacity={variant === 'white' ? 0.9 : 1}
            />

            {/* Handle - Slim & Tall */}
            <rect
                x="116"
                y="222"
                width="24"
                height="32"
                rx="1"
                fill={variant === 'white' ? "#F0F0F0" : "url(#handleGradient)"}
                fillOpacity={variant === 'white' ? 0.9 : 1}
            />
        </svg>
    );
};

export default VelLogo;
